import { isNil } from 'lodash';
import React, { useContext } from 'react';
import { StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBUserImage } from '@shared/components/image';
import { MBTextBody2 } from '@shared/components/typography';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { ColorThemeContext, grid } from '@shared/styles';
import { formatDateTime } from '@shared/util/date';
import { useIsDesktop } from '@shared/util/hooks';

interface Props {
  user?: MBAPI.ShortUser;
  date?: string;
  indent?: boolean;
  collapsed?: boolean;
}

/**
 * This component displays a user and date in a compact or expanded format. This
 * is used to show metadata below other components like the GeolocationField or
 * SignatureField.
 */

export function InputUserAndDate({ user, date, indent, collapsed }: Props) {
  if (isNil(user) && isNil(date)) {
    return null;
  }

  if (collapsed) {
    return <UserAndDateCollapsed user={user} date={date} />;
  } else {
    return <UserAndDateExpanded user={user} date={date} indent={indent} />;
  }
}

function UserAndDateCollapsed({ user, date }: Props) {
  const colors = useContext(ColorThemeContext);

  return (
    <View fill>
      {!isNil(user) && (
        <View row centerVertically paddingTop={0.5} fill>
          <MBUserImage user={user} style={styles.avatar} />
          <MBView style={styles.nameCollapsed}>
            <MBTextBody2 color={colors.foregroundActive} numberOfLines={1}>
              {user?.fullName}
            </MBTextBody2>
          </MBView>
        </View>
      )}
      {!isNil(date) && (
        <MBTextBody2 color={colors.foregroundActive}>{formatDateTime(date)}</MBTextBody2>
      )}
    </View>
  );
}

function UserAndDateExpanded({ user, date, indent }: Props) {
  const responsiveForms = useResponsiveFormContext();
  const isDesktop = useIsDesktop();
  const colors = useContext(ColorThemeContext);

  return (
    <MBView
      row={isDesktop}
      centerVertically
      paddingTop={1}
      paddingLeft={indent ? 7 : 0}
      paddingBottom={responsiveForms ? 1 : 0}
      style={isDesktop ? styles.metadataDesktop : styles.metadataMobile}
    >
      {!isNil(user) && (
        <View row centerVertically>
          <MBUserImage
            user={user}
            style={[styles.avatar, !isDesktop && !responsiveForms && styles.avatarMobile]}
          />
          <MBView style={isDesktop ? styles.nameDesktop : styles.nameMobile}>
            <MBTextBody2
              numberOfLines={1}
              color={responsiveForms ? colors.foregroundActive : colors.foregroundInactive}
            >
              {user?.fullName}
            </MBTextBody2>
          </MBView>
        </View>
      )}
      {!isNil(user) && !isNil(date) && isDesktop && (
        <View paddingHorizontal={1}>
          <MBTextBody2 color={colors.foregroundInactive}>·</MBTextBody2>
        </View>
      )}
      {!isNil(date) && (
        <MBTextBody2 color={colors.foregroundInactive}>{formatDateTime(date)}</MBTextBody2>
      )}
    </MBView>
  );
}

const styles = StyleSheet.create({
  metadataDesktop: {
    flexWrap: 'nowrap',
  },
  metadataMobile: {
    flexWrap: 'wrap',
  },
  nameCollapsed: {
    overflow: 'hidden',
    maxWidth: grid(16),
  },
  nameDesktop: {
    overflow: 'hidden',
    maxWidth: grid(24),
  },
  nameMobile: {
    overflow: 'hidden',
    maxWidth: grid(30),
  },
  avatar: {
    width: grid(2),
    height: grid(2),
    borderRadius: grid(1),
    overflow: 'hidden',
    marginRight: grid(1),
  },
  avatarMobile: {
    marginLeft: -grid(3),
  },
});
